import { useEffect, useRef, useState } from 'react';
import { TierRow, CHIP_COLORS, InlineEditor } from './TierRow';
import { isDone } from './tiers';
import { isNewItem } from './counts';
import { groupByProject, subtreeCounts, flattenTree, canAddChild, nextPaletteColor, searchCards, PROJECT_PALETTE, NO_PROJECT, NO_PROJECT_COLOR } from './projects';
import './ProjectsView.css';

// ============================================================
// PROJECTS VIEW — Phase B: editable. One headline band per top-level
// project — the name set huge in the project's color; sub-projects one step
// down, sub-subs one more; a spine in the hue ties every child to its
// project; cards hang off the spine as TierRow light rows. Every band is a
// disclosure, collapsed by default (NO PROJECT open), remembered per
// ProjectId in localStorage under OPEN_KEY.
//
// Collapsed, a band carries its summary on the right: live cards over the
// whole subtree (done cards never counted) split by court, "· N new" for
// the NEW rail's untriaged cards, and a NOW / TODAY badge when any
// descendant sits in that tier — `tierOf(id)` comes from tier() over the
// board. Expanded: cards directly on the project (NEW ones badged NEW),
// then the "+ sub-project" ghost row, the sub-project bands (collapsed),
// then the done group.
//
// EDITS (Phase B) — every write goes back through the parent, which holds
// it behind the 5 s undo toast:
//   drag       any row, mouse or touch (long-press 300 ms), onto any band —
//              collapsed bands accept without opening; Esc cancels; a drop
//              off-target is a no-op                       → onMove(item, projectId)
//   ⋯ menu     Move to… (picker), Rename (inline), Merge into… (picker +
//              inline confirm)                              → onMove / onRename / onMerge
//   band       pencil (hover, by the id) → inline rename    → onRenameProject(node, name)
//   + PROJECT  ghost band above NO PROJECT; "+ sub-project" ghost row in
//              every open band (depth < 3)                  → onCreateProject({ name, parentId, color })
//
// `items` must be EVERY card, not the board's collapsed list: a same-court
// child folded under its parent on the board is its own row here.
// Pure presentation otherwise: no fetch, no MSAL.
// ============================================================

export const OPEN_KEY = 'watch.projects.open';
export const DRAG_THRESHOLD_PX = 5;
export const LONG_PRESS_MS = 300;
export const FLASH_MS = 20000;
const DEFAULT_OPEN = { [NO_PROJECT]: true };
const DROP_NONE = '__none__';                 // data-drop-id for the NO PROJECT band ('' is not a usable attribute value)
const dropIdOf = (id) => (id === NO_PROJECT ? DROP_NONE : id);
const projectIdOf = (dropId) => (dropId === DROP_NONE ? NO_PROJECT : dropId);

// localStorage is a per-viewer convenience: it can be empty or throw (a
// private window, blocked site data), and the view must render either way.
export function readOpen(storage = typeof localStorage !== 'undefined' ? localStorage : null) {
  try {
    const raw = storage?.getItem(OPEN_KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    return parsed && typeof parsed === 'object' ? { ...DEFAULT_OPEN, ...parsed } : { ...DEFAULT_OPEN };
  } catch { return { ...DEFAULT_OPEN }; }
}
export function writeOpen(open, storage = typeof localStorage !== 'undefined' ? localStorage : null) {
  try { storage?.setItem(OPEN_KEY, JSON.stringify(open)); } catch { /* per-viewer convenience only */ }
}

// ── drag ──
// Hand-rolled on Pointer Events (one path for mouse, pen and touch), the
// FIGRenewalPipeline strip's shape: a threshold separates a click from a
// drag; on touch a 300 ms press must land first, and a move before that is
// a scroll, not a drag. While engaged, document listeners track the pointer,
// the band under it (elementFromPoint → [data-drop-id]) is the target, Esc
// cancels, and the click that follows the pointerup is swallowed so the row
// does not also toggle open.
function useRowDrag({ onDrop }) {
  const [drag, setDrag] = useState(null);           // { item, x, y, target } while engaged
  const ref = useRef(null);                         // { item, startX, startY, type, timer, engaged, target }
  const justDropped = useRef(false);                // the click after a drop is swallowed by the row (rowProps below)

  const findTarget = (x, y) => {
    const el = typeof document.elementFromPoint === 'function' ? document.elementFromPoint(x, y) : null;
    const band = el?.closest?.('[data-drop-id]');
    return band ? band.getAttribute('data-drop-id') : null;
  };
  const cleanup = () => {
    const d = ref.current;
    if (!d) return;
    clearTimeout(d.timer);
    document.removeEventListener('pointermove', d.onMove);
    document.removeEventListener('pointerup', d.onUp);
    document.removeEventListener('pointercancel', d.onCancel);
    document.removeEventListener('keydown', d.onKey);
    document.removeEventListener('touchmove', d.onTouchMove);
    ref.current = null;
    setDrag(null);
  };
  const engage = (x, y) => {
    const d = ref.current;
    if (!d || d.engaged) return;
    d.engaged = true;
    d.target = findTarget(x, y);
    setDrag({ item: d.item, x, y, target: d.target });
  };
  const start = (e, item) => {
    if (e.button != null && e.button !== 0) return;
    if (e.target.closest && e.target.closest('.TierRow-pin, .TierRow-menu, .TierRow-edit, button, input, a')) return;
    cleanup();
    const d = { item, startX: e.clientX, startY: e.clientY, type: e.pointerType || 'mouse', engaged: false, target: null, timer: null };
    d.onMove = (ev) => {
      const dx = ev.clientX - d.startX, dy = ev.clientY - d.startY;
      if (!d.engaged) {
        if (Math.abs(dx) < DRAG_THRESHOLD_PX && Math.abs(dy) < DRAG_THRESHOLD_PX) return;
        if (d.type === 'touch') { cleanup(); return; }           // moved before the press landed: a scroll
        engage(ev.clientX, ev.clientY);
      }
      d.target = findTarget(ev.clientX, ev.clientY);
      setDrag({ item: d.item, x: ev.clientX, y: ev.clientY, target: d.target });
    };
    d.onUp = (ev) => {
      const was = d.engaged;
      const target = was ? findTarget(ev.clientX, ev.clientY) ?? d.target : null;
      cleanup();
      if (!was) return;
      justDropped.current = true;
      setTimeout(() => { justDropped.current = false; }, 0);
      if (target != null) onDrop(item, projectIdOf(target));
    };
    d.onCancel = () => cleanup();
    d.onKey = (ev) => { if (ev.key === 'Escape') cleanup(); };
    d.onTouchMove = (ev) => { if (d.engaged && ev.cancelable) ev.preventDefault(); };
    if (d.type === 'touch') d.timer = setTimeout(() => engage(d.startX, d.startY), LONG_PRESS_MS);
    ref.current = d;
    document.addEventListener('pointermove', d.onMove);
    document.addEventListener('pointerup', d.onUp);
    document.addEventListener('pointercancel', d.onCancel);
    document.addEventListener('keydown', d.onKey);
    document.addEventListener('touchmove', d.onTouchMove, { passive: false });
  };
  const onClickCapture = (e) => { if (justDropped.current) { justDropped.current = false; e.stopPropagation(); e.preventDefault(); } };
  useEffect(() => cleanup, []);   // eslint-disable-line react-hooks/exhaustive-deps
  return { drag, start, onClickCapture };
}

// ── pickers ──
// A compact list with a filter box. `options`: [{ id, label, color?, depth?, hint? }].
// Typing filters on label + hint; Enter picks the first match; Esc cancels.
// A cursor rides the list: ↑/↓ move it, Enter picks the highlighted row
// (the first by default), and `onHighlight(id)` reports each move.
export function Picker({ options, placeholder, onPick, onCancel, onHighlight, emptyText = 'no match', testId = 'picker' }) {
  const [q, setQ] = useState('');
  const [cursor, setCursor] = useState(0);
  const ref = useRef(null);
  useEffect(() => { ref.current?.focus(); }, []);
  const needle = q.trim().toLowerCase();
  const shown = options.filter(o => !needle || `${o.label} ${o.hint || ''}`.toLowerCase().includes(needle));
  const at = Math.min(cursor, Math.max(0, shown.length - 1));
  const move = (d) => { const next = Math.max(0, Math.min(shown.length - 1, at + d)); setCursor(next); if (shown[next]) onHighlight?.(shown[next].id); };
  return (
    <div className="PV-picker" data-testid={testId} onClick={e => e.stopPropagation()} onPointerDown={e => e.stopPropagation()}>
      <input ref={ref} className="PV-picker-input" placeholder={placeholder} aria-label={placeholder} value={q} onChange={e => { setQ(e.target.value); setCursor(0); }}
        onKeyDown={e => {
          e.stopPropagation();
          if (e.key === 'Escape') { e.preventDefault(); onCancel(); }
          if (e.key === 'ArrowDown') { e.preventDefault(); move(1); }
          if (e.key === 'ArrowUp') { e.preventDefault(); move(-1); }
          if (e.key === 'Enter') { e.preventDefault(); if (shown[at]) onPick(shown[at].id); }
        }} />
      <div className="PV-picker-list" role="listbox">
        {shown.map((o, n) => (
          <button key={o.id || DROP_NONE} type="button" role="option" aria-selected={n === at} className={'PV-picker-row' + (n === at ? ' PV-picker-row--cursor' : '')} style={{ '--tick': o.color || 'transparent', paddingLeft: `${10 + (o.depth || 0) * 14}px` }}
            onMouseEnter={() => { setCursor(n); onHighlight?.(o.id); }} onClick={() => onPick(o.id)}>
            {o.color && <span className="PV-picker-tick" aria-hidden="true" />}
            <span className="PV-picker-label">{o.label}</span>
            {o.hint && <span className="PV-picker-hint">{o.hint}</span>}
          </button>
        ))}
        {shown.length === 0 && <div className="PV-picker-empty">{emptyText}</div>}
      </div>
    </div>
  );
}

// ── the PROJECTS search ──
// One box above the bands: type a WatchId (#N), a title, a project, who,
// an insured, or words from the synopsis; the top eight land in a list
// under it (searchCards, projects.js). ↑/↓/Enter or a click picks; Esc
// clears the text and closes, the box keeps focus. The pick goes up as a
// WatchId; the parent turns it into a `locate`.
export function ProjectSearch({ items, labelOf, colorOf, onPick, testId = 'project-search' }) {
  const [q, setQ] = useState('');
  const [cursor, setCursor] = useState(0);
  const ref = useRef(null);
  const results = q.trim() ? searchCards(items, q, { labelOf }) : [];
  const at = Math.min(cursor, Math.max(0, results.length - 1));
  const clear = () => { setQ(''); setCursor(0); ref.current?.focus(); };
  const pick = (id) => { clear(); onPick?.(id); };
  return (
    <div className="PV-search" data-testid={testId} onClick={e => e.stopPropagation()}>
      <div className="PV-search-box">
        <span className="PV-search-glyph" aria-hidden="true">⌕</span>
        <input ref={ref} className="PV-search-input" type="search" placeholder="Find a card — #N, title, project, who, insured" aria-label="Find a card"
          value={q} onChange={e => { setQ(e.target.value); setCursor(0); }}
          onKeyDown={e => {
            if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); clear(); }
            if (e.key === 'ArrowDown') { e.preventDefault(); setCursor(Math.min(results.length - 1, at + 1)); }
            if (e.key === 'ArrowUp') { e.preventDefault(); setCursor(Math.max(0, at - 1)); }
            if (e.key === 'Enter') { e.preventDefault(); if (results[at]) pick(results[at].item.id); }
          }} />
      </div>
      {q.trim() && (
        <div className="PV-picker-list" role="listbox" aria-label="Cards">
          {results.map((r, n) => (
            <button key={r.item.id} type="button" role="option" aria-selected={n === at} className={'PV-picker-row' + (n === at ? ' PV-picker-row--cursor' : '')}
              style={{ '--tick': colorOf?.(r.item) || NO_PROJECT_COLOR }} onMouseEnter={() => setCursor(n)} onClick={() => pick(r.item.id)}>
              <span className="PV-picker-tick" aria-hidden="true" />
              <span className="PV-search-id">#{r.item.id}</span>
              <span className="PV-picker-label">{r.item.title}</span>
              <span className="PV-search-why">{r.why}</span>
            </button>
          ))}
          {results.length === 0 && <div className="PV-picker-empty">no match</div>}
        </div>
      )}
    </div>
  );
}

const projectOptions = (tree) => [
  ...flattenTree(tree).map(n => ({ id: n.id, label: n.name, color: n.color, depth: n.depth - 1, hint: n.depth > 1 ? n.path.slice(0, -1).join(' › ') : '' })),
  { id: NO_PROJECT, label: 'No project', color: NO_PROJECT_COLOR, depth: 0 },
];
const cardOptions = (items, exceptId) => items
  .filter(i => i.status === 'Open' && i.id !== exceptId)
  .sort((a, b) => b.id - a.id)
  .map(i => ({ id: i.id, label: i.title || `#${i.id}`, hint: `#${i.id}` }));

// The ⋯ menu on a row: Move to…, Rename, Merge into…. The parent owns which
// row has it open (`state`), so only one is ever open.
function RowMenu({ item, state, setState, tree, items, onMove, onRename, onMerge }) {
  const open = state?.id === item.id;
  const mode = open ? state.mode : null;
  const close = () => setState(null);
  return (
    <>
      <button type="button" className="TierRow-menu-btn" aria-label={`Actions for #${item.id}`} aria-haspopup="menu" aria-expanded={open}
        onClick={(e) => { e.stopPropagation(); setState(open ? null : { id: item.id, mode: 'menu' }); }}>⋯</button>
      {open && mode === 'menu' && (
        <div className="PV-menu" role="menu" data-testid={`row-menu-${item.id}`} onClick={e => e.stopPropagation()} onPointerDown={e => e.stopPropagation()}>
          <button type="button" role="menuitem" className="PV-menu-item" onClick={() => setState({ id: item.id, mode: 'move' })}>Move to…</button>
          <button type="button" role="menuitem" className="PV-menu-item" onClick={() => { close(); onRename(item); }}>Rename</button>
          <button type="button" role="menuitem" className="PV-menu-item" onClick={() => setState({ id: item.id, mode: 'merge' })}>Merge into…</button>
        </div>
      )}
      {open && mode === 'move' && (
        <Picker testId={`move-picker-${item.id}`} placeholder="Move to…" options={projectOptions(tree)} onCancel={close}
          onPick={(pid) => { close(); onMove(item, pid); }} />
      )}
      {open && mode === 'merge' && (
        <Picker testId={`merge-picker-${item.id}`} placeholder="Merge into… (#N or title)" options={cardOptions(items, item.id)} onCancel={close}
          onPick={(tid) => setState({ id: item.id, mode: 'confirm', target: tid })} />
      )}
      {open && mode === 'confirm' && (
        <div className="PV-menu PV-confirm" data-testid={`merge-confirm-${item.id}`} onClick={e => e.stopPropagation()} onPointerDown={e => e.stopPropagation()}>
          <div className="PV-confirm-text">Merge <b>#{item.id}</b> into <b>#{state.target}</b> — its thread moves there and #{item.id} closes.</div>
          <div className="PV-confirm-btns">
            <button type="button" className="PV-btn PV-btn--go" onClick={() => { close(); onMerge(item, state.target); }}>Merge</button>
            <button type="button" className="PV-btn" onClick={close}>Cancel</button>
          </div>
        </div>
      )}
    </>
  );
}

// ── rows ──
// Rows in a node: NOW first, TODAY, then by heat, then id. NEW cards carry
// no heat and no tier, so they fall to the end of the live rows.
const TIER_RANK = { NOW: 0, TODAY: 1 };
function orderOpen(items, tierOf, heatMap) {
  const rank = (i) => TIER_RANK[tierOf(i.id)] ?? 2;
  return [...items].sort((a, b) => rank(a) - rank(b) || ((heatMap?.[b.id] || 0) - (heatMap?.[a.id] || 0)) || a.id - b.id);
}
const liveRows = (g) => [...g.open, ...(g.fresh || [])];
const badgeFor = (g, tierOf) => (i) => ((g.fresh || []).includes(i) ? 'NEW' : (tierOf(i.id) || ''));

function rowEntry(item, badge, heatMap) {
  return { item, heat: heatMap?.[item.id] || 0, reasons: [], reason: '', fill: '', pinned: item.pinned === true, badge };
}

function Rows({ items, badgeOf, ctx }) {
  const { heatMap, colorOf, onPin, expandedId, onToggle, renderCard, drag, startDrag, dragClickCapture, menuState, setMenuState, renaming, setRenaming, tree, allItems, onMove, onRename, onMerge, editable, flashId } = ctx;
  if (!items.length) return null;
  return (
    <div className="TierRows PV-rows">
      {items.map(i => (
        <TierRow key={i.id} entry={rowEntry(i, badgeOf(i), heatMap)} onPin={onPin} colorOf={colorOf}
          expanded={expandedId === i.id} onToggle={onToggle} renderCard={renderCard}
          lifted={drag?.item?.id === i.id} flash={flashId === i.id}
          rowProps={editable ? { onPointerDown: (e) => startDrag(e, i), onClickCapture: dragClickCapture } : null}
          editTitle={renaming === i.id}
          onRenameCommit={(item, t) => { setRenaming(null); onRename(item, t); }}
          onRenameCancel={() => setRenaming(null)}
          menu={editable ? <RowMenu item={i} state={menuState} setState={setMenuState} tree={tree} items={allItems}
            onMove={onMove} onRename={(item) => setRenaming(item.id)} onMerge={onMerge} /> : null} />
      ))}
    </div>
  );
}

function DoneGroup({ items, open, onDisclose, ctx }) {
  if (!items.length) return null;
  return (
    <div className="PV-done">
      <button type="button" className="PV-done-toggle" aria-expanded={open} onClick={onDisclose}>
        <span className="PV-done-caret" aria-hidden="true">{open ? '▴' : '▾'}</span> done ({items.length})
      </button>
      {open && <Rows items={[...items].sort((a, b) => b.id - a.id)} badgeOf={() => 'DONE'} ctx={ctx} />}
    </div>
  );
}

// "13 cards · 3 mine · 6 theirs · 4 new" — done cards excluded; NEW cards
// are in the total and in "new", never in the court split.
export function summaryText(c) {
  const parts = [`${c.open} card${c.open === 1 ? '' : 's'}`];
  if (c.mine) parts.push(`${c.mine} mine`);
  if (c.theirs) parts.push(`${c.theirs} theirs`);
  if (c.holding) parts.push(`${c.holding} holding`);
  if (c.fresh) parts.push(`${c.fresh} new`);
  return parts.join(' · ');
}

// ── bands ──
// The disclosure band and drop target. `depth` 1..3 sets the type scale;
// NO PROJECT is a depth-1 band in the neutral slate. The pencil (hover, by
// the id) opens the inline rename; the name click stays the toggle.
function Band({ id, name, depth, counts, open, onToggle, testId, drag, editing, onEditStart, onRenameCommit, onRenameCancel, editable, flash = false }) {
  const targeting = !!drag;
  const targeted = targeting && drag.target === dropIdOf(id);
  const toggle = () => { if (!editing) onToggle(); };
  return (
    <div className={`PV-band PV-band--d${depth}${open ? ' PV-band--open' : ''}${targeting ? ' PV-band--targeting' : ''}${targeted ? ' PV-band--target' : ''}${flash ? ' PV-band--flash' : ''}`}
      role="button" tabIndex={0} aria-expanded={open} data-drop-id={dropIdOf(id)}
      onClick={toggle} onKeyDown={(e) => { if (editing) return; if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onToggle(); } }}
      data-testid={testId}>
      <span className="PV-band-caret" aria-hidden="true">{open ? '▾' : '▸'}</span>
      {editing ? (
        <span className="PV-band-name PV-band-name--editing">
          <InlineEditor initial={name} className="PV-band-edit" label="Project name" onCommit={onRenameCommit} onCancel={onRenameCancel} />
        </span>
      ) : (
        <span className="PV-band-name">{name}</span>
      )}
      {!open && !editing && (
        <span className="PV-band-summary" data-testid={`${testId}-summary`}>
          {counts.tier && <span className="PV-band-tier" style={{ '--chip': CHIP_COLORS[counts.tier] }}>{counts.tier}</span>}
          <span className="PV-band-counts">{summaryText(counts)}</span>
        </span>
      )}
      {editable && id !== NO_PROJECT && !editing && (
        <button type="button" className="PV-band-pencil" aria-label={`Rename ${name}`} title="Rename"
          onClick={(e) => { e.stopPropagation(); onEditStart(); }} onPointerDown={e => e.stopPropagation()}>✎</button>
      )}
      <span className="PV-band-id" title="ProjectId">{id}</span>
    </div>
  );
}

// Inline creator: a name, a swatch row from PROJECT_PALETTE, Enter creates.
function Creator({ parentId, defaultColor, onCreate, onCancel, testId }) {
  const [name, setName] = useState('');
  const [color, setColor] = useState(defaultColor);
  const ref = useRef(null);
  useEffect(() => { ref.current?.focus(); }, []);
  const commit = () => { const n = name.trim(); if (n) onCreate({ name: n, parentId, color }); else onCancel(); };
  return (
    <div className="PV-creator" data-testid={testId} style={{ '--proj': color }} onClick={e => e.stopPropagation()} onPointerDown={e => e.stopPropagation()}>
      <input ref={ref} className="PV-creator-name" placeholder={parentId ? 'Sub-project name' : 'Project name'} aria-label={parentId ? 'Sub-project name' : 'Project name'}
        value={name} onChange={e => setName(e.target.value)}
        onKeyDown={e => { e.stopPropagation(); if (e.key === 'Enter') { e.preventDefault(); commit(); } if (e.key === 'Escape') { e.preventDefault(); onCancel(); } }} />
      <div className="PV-swatches" role="radiogroup" aria-label="Color">
        {PROJECT_PALETTE.map(c => (
          <button key={c} type="button" role="radio" aria-checked={c === color} aria-label={c} className={'PV-swatch' + (c === color ? ' PV-swatch--on' : '')}
            style={{ background: c }} onClick={() => setColor(c)} />
        ))}
      </div>
      <button type="button" className="PV-btn PV-btn--go" onClick={commit}>Create</button>
      <button type="button" className="PV-btn" onClick={onCancel}>Cancel</button>
    </div>
  );
}

// One node: its band; when open, its own rows, the sub-project ghost, its
// children (bands), its done group.
function ProjectNode({ node, ctx }) {
  const { groups, tree, tierOf, openMap, toggleOpen, doneOpen, toggleDone, drag, bandEdit, setBandEdit, onRenameProject, creating, setCreating, onCreateProject, editable, flashChain } = ctx;
  const g = groups.get(node.id) || { open: [], fresh: [], done: [] };
  const counts = subtreeCounts(tree, groups, node.id, tierOf);
  const open = !!openMap[node.id];
  const badgeOf = badgeFor(g, tierOf);
  const canSub = editable && canAddChild(tree, node.id);
  return (
    <section className={`PV-node PV-node--d${node.depth}${node.depth === 1 ? ' PV-project' : ' PV-sub'}${open ? ' PV-node--open' : ''}`}
      style={{ '--proj': node.color }} data-testid={`pv-${node.id}`}>
      <Band id={node.id} name={node.name} depth={node.depth} counts={counts} open={open} onToggle={() => toggleOpen(node.id)} testId={`pv-band-${node.id}`}
        drag={drag} editable={editable} editing={bandEdit === node.id} onEditStart={() => setBandEdit(node.id)} flash={flashChain.has(node.id)}
        onRenameCommit={(n) => { setBandEdit(null); onRenameProject(node, n); }} onRenameCancel={() => setBandEdit(null)} />
      {open && (
        <div className="PV-body">
          <Rows items={orderOpen(liveRows(g), tierOf, ctx.heatMap)} badgeOf={badgeOf} ctx={ctx} />
          {canSub && (creating === node.id
            ? <Creator parentId={node.id} defaultColor={node.color} testId={`creator-${node.id}`} onCancel={() => setCreating(null)}
                onCreate={(p) => { setCreating(null); onCreateProject(p); }} />
            : <button type="button" className="PV-ghost PV-ghost--sub" onClick={() => setCreating(node.id)}>+ sub-project</button>)}
          {node.children.map(c => <ProjectNode key={c.id} node={c} ctx={ctx} />)}
          <DoneGroup items={g.done} open={!!doneOpen[node.id]} onDisclose={() => toggleDone(node.id)} ctx={ctx} />
          {liveRows(g).length + g.done.length + node.children.length === 0 && !canSub && <div className="PV-empty">nothing here yet</div>}
        </div>
      )}
    </section>
  );
}

/**
 * @param items            EVERY item (all statuses, NEW included, collapsed children included)
 * @param tree             projects.js tree
 * @param tierOf           (id) => 'NOW' | 'TODAY' | undefined — from tier() over the board
 * @param colorOf          (item) => hex — the bar color for a row
 * @param onMove           (item, projectId | '') — Phase B
 * @param onRename         (item, title)
 * @param onMerge          (item, targetWatchId)
 * @param onRenameProject  (node, name)
 * @param onCreateProject  ({ name, parentId, color })
 * @param locate           { id, n } — open the card's project chain, scroll its row into view, flash; n is fresh per request
 * @param onFlashEnd       optional, called when the flash clears (a click or FLASH_MS)
 * @param storage          localStorage stand-in (tests); undefined uses the real one
 */
export default function ProjectsView({ items, tree, tierOf, heatMap, colorOf, onPin, expandedId = null, onToggle, renderCard, storage,
  onMove, onRename, onMerge, onRenameProject, onCreateProject, locate = null, onFlashEnd }) {
  const editable = typeof onMove === 'function';
  const [openMap, setOpenMap] = useState(() => readOpen(storage));
  const toggleOpen = (id) => setOpenMap(o => { const next = { ...o, [id]: !o[id] }; writeOpen(next, storage); return next; });
  const openMany = (ids) => setOpenMap(o => { const next = { ...o }; for (const id of ids) next[id] = true; writeOpen(next, storage); return next; });
  const [doneOpen, setDoneOpen] = useState({});
  const toggleDone = (id) => setDoneOpen(o => ({ ...o, [id]: !o[id] }));

  // ── locate + flash (the PROJECTS search) ──
  // A located card: every ancestor band opens (persisted like a click), the
  // done group too when the card is done, the row scrolls to centre two
  // frames later, and the chain + row carry the flash until any click or
  // FLASH_MS. The click that started the locate is already over when the
  // listener is armed on the next frame, so it never clears its own flash.
  const [flashId, setFlashId] = useState(null);
  const chainOf = (item) => {
    if (!item) return [];
    const ids = [];
    let node = item.projectId ? tree?.byId?.get(item.projectId) : null;
    if (!node) return [NO_PROJECT];
    for (let i = 0; node && i < 3; i++) { ids.unshift(node.id); node = node.parentId ? tree.byId.get(node.parentId) : null; }
    return ids;
  };
  useEffect(() => {
    if (!locate || locate.id == null) return undefined;
    const item = (items || []).find(i => i.id === locate.id);
    if (!item) return undefined;
    const chain = chainOf(item);
    openMany(chain);
    if (isDone(item)) setDoneOpen(o => ({ ...o, [chain[chain.length - 1]]: true }));
    let raf2 = 0;
    const raf1 = requestAnimationFrame(() => { raf2 = requestAnimationFrame(() => {
      document.querySelector(`[data-testid="tier-${item.id}"]`)?.scrollIntoView?.({ block: 'center', behavior: 'smooth' });
    }); });
    setFlashId(item.id);
    return () => { cancelAnimationFrame(raf1); cancelAnimationFrame(raf2); };
  }, [locate?.n]);   // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (flashId == null) return undefined;
    let timer = 0;
    const clear = () => { setFlashId(null); onFlashEnd?.(); };
    const raf = requestAnimationFrame(() => {
      timer = setTimeout(clear, FLASH_MS);
      document.addEventListener('click', clear, { capture: true });
    });
    return () => { cancelAnimationFrame(raf); clearTimeout(timer); document.removeEventListener('click', clear, { capture: true }); };
  }, [flashId]);   // eslint-disable-line react-hooks/exhaustive-deps
  const flashItem = flashId != null ? (items || []).find(i => i.id === flashId) : null;
  const flashChain = new Set(flashItem ? chainOf(flashItem) : []);
  const [menuState, setMenuState] = useState(null);     // { id, mode: 'menu'|'move'|'merge'|'confirm', target? }
  const [renaming, setRenaming] = useState(null);       // card id with the inline title editor open
  const [bandEdit, setBandEdit] = useState(null);       // project id with the inline name editor open
  const [creating, setCreating] = useState(null);       // parent id ('' = top level) with the creator open
  const { drag, start: startDrag, onClickCapture: dragClickCapture } = useRowDrag({ onDrop: (item, pid) => { if ((item.projectId || NO_PROJECT) !== pid) onMove?.(item, pid); } });
  // Any click outside a menu / picker closes it.
  useEffect(() => {
    if (!menuState) return undefined;
    const onDoc = (e) => { if (!e.target.closest?.('.TierRow-menu')) setMenuState(null); };
    document.addEventListener('pointerdown', onDoc);
    return () => document.removeEventListener('pointerdown', onDoc);
  }, [menuState]);

  const groups = groupByProject(items, tree, { isDone, isNew: isNewItem });
  const none = groups.get(NO_PROJECT) || { open: [], fresh: [], done: [] };
  const noneCounts = subtreeCounts(tree, groups, NO_PROJECT, tierOf);
  const noneOpen = !!openMap[NO_PROJECT];
  const ctx = {
    groups, tree, tierOf, heatMap, colorOf, onPin, expandedId, onToggle, renderCard, openMap, toggleOpen, doneOpen, toggleDone,
    drag, startDrag, dragClickCapture, menuState, setMenuState, renaming, setRenaming, bandEdit, setBandEdit, creating, setCreating,
    allItems: items, editable, onMove, onRename, onMerge, onRenameProject, onCreateProject, flashId, flashChain,
  };
  return (
    <div className={'ProjectsView' + (drag ? ' ProjectsView--dragging' : '')} data-testid="projects-view">
      {tree.roots.length === 0 && <div className="Watch-empty">No projects yet — add one below.</div>}
      {tree.roots.map(r => <ProjectNode key={r.id} node={r} ctx={ctx} />)}
      {editable && (creating === NO_PROJECT
        ? <Creator parentId="" defaultColor={nextPaletteColor(tree)} testId="creator-root" onCancel={() => setCreating(null)}
            onCreate={(p) => { setCreating(null); onCreateProject(p); }} />
        : <button type="button" className="PV-ghost PV-ghost--root" onClick={() => setCreating(NO_PROJECT)}>+ PROJECT</button>)}
      <section className={`PV-node PV-node--d1 PV-project PV-none${noneOpen ? ' PV-node--open' : ''}`} style={{ '--proj': NO_PROJECT_COLOR }} data-testid="pv-none">
        <Band id="" name="No project" depth={1} counts={noneCounts} open={noneOpen} onToggle={() => toggleOpen(NO_PROJECT)} testId="pv-band-none" drag={drag} editable={editable} flash={flashChain.has(NO_PROJECT)} />
        {noneOpen && (
          <div className="PV-body">
            <Rows items={orderOpen(liveRows(none), tierOf, heatMap)} badgeOf={badgeFor(none, tierOf)} ctx={ctx} />
            <DoneGroup items={none.done} open={!!doneOpen[NO_PROJECT]} onDisclose={() => toggleDone(NO_PROJECT)} ctx={ctx} />
            {liveRows(none).length + none.done.length === 0 && <div className="PV-empty">every card has a project</div>}
          </div>
        )}
      </section>
      {drag && (
        <div className="PV-drag-ghost" style={{ left: drag.x, top: drag.y }} aria-hidden="true" data-testid="drag-ghost">
          #{drag.item.id} {drag.item.title}
        </div>
      )}
    </div>
  );
}
